import React, { useEffect, useRef, useState } from 'react'
import Input from 'components/input'
import Button from 'components/button'
import Modal, { ModalProps } from 'components/modal'
import { Copy } from 'components/icons'
import { useApplicationStore } from 'hooks/application'

interface DeleteResourceProps extends ModalProps {
  resourceName: string
  deleteResource: () => Promise<void>
  restoring?: boolean
  notUndo?: boolean
  otherContent?: React.ReactNode
  onOpen?: () => void
  disableCopy?: boolean
  customHeading?: string
  /** The confirm button's text, for a deletion the resource wording doesn't fit (a folder). */
  confirmLabel?: string
  /** The confirm button's text while it works, with confirmLabel (default "Deleting…"). */
  submittingLabel?: string
  /**
   * Whether the user must type the resource's name to confirm (default true). Without it the
   * dialog is a plain confirm: otherContent as the body, no warning banner, and a Cancel button
   * that takes focus.
   */
  confirmByTyping?: boolean
  closeOnSuccess?: boolean // default true
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

const DeleteModal: React.FC<DeleteResourceProps> = ({
  id,
  open,
  setOpen,
  resourceName,
  onClose,
  deleteResource,
  restoring,
  notUndo,
  otherContent,
  onOpen,
  disableCopy,
  customHeading,
  confirmLabel,
  submittingLabel = 'Deleting…',
  confirmByTyping = true,
  closeOnSuccess = true,
  onSuccess,
  onError
}) => {
  const [deleteName, setDeleteName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { setSnackbarMessage } = useApplicationStore()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const confirmDeleteResource = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await deleteResource() // wait for parent’s API work
    } catch (e) {
      if (onError) onError(e) // parent can show extra UI if needed
      // keep the modal open on error so user can retry
      setSubmitting(false)
      setDeleteName('')
      return
    }
    if (onSuccess) onSuccess()
    if (closeOnSuccess) setOpen(false) // modal closes itself on success
    setSubmitting(false)
    setDeleteName('')
  }

  useEffect(() => {
    if (open) onOpen?.()
    // A plain confirm starts on Cancel, the safe choice, so Enter doesn't delete by accident.
    if (open && !confirmByTyping) cancelRef.current?.focus()
    return () => {
      setDeleteName('')
      onClose?.()
    }
  }, [open])

  const disabled = submitting || (confirmByTyping && deleteName !== resourceName)

  const buttonLabel = (): string => {
    if (confirmLabel) return submitting ? submittingLabel : confirmLabel
    if (submitting) {
      if (restoring) return 'Restoring…'
      return notUndo ? 'Deleting…' : 'Archiving…'
    }
    if (notUndo) return 'I understand the consequences, permanently delete this resource'
    return restoring ? 'Yes, I want to restore this resource' : 'I understand the consequences, archive this resource'
  }

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      title={
        customHeading ??
        `Are you sure you want to ${notUndo ? 'permanently delete' : restoring ? 'restore' : 'archive'} this resource?`
      }
      warningBanner={confirmByTyping}
      // Prevent closing via overlay/esc while submitting (if Modal supports it, otherwise guard in handler)
      onClose={submitting ? undefined : onClose}
    >
      {!confirmByTyping && otherContent}
      {confirmByTyping && (
        <div>
          <div>
            To confirm your action please type the name of the resource in the box below.{' '}
            {notUndo ? 'You are not able to reverse this action so please double check it is correct.' : ''}
          </div>

          {otherContent ?? null}

          <div className="flex flex-row items-center flex-wrap rounded shadow w-full p-5 select-none mt-[30px] mb-[30px]">
            <div data-cy="resource-name-text">{resourceName}</div>
            {!disableCopy && (
              <div
                className="ml-auto cursor-pointer"
                title="Copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(resourceName)
                    setSnackbarMessage({ status: 'success', message: 'Resource name copied!' })
                  } catch {
                    // clipboard permission denied — silently ignore
                  }
                }}
              >
                <Copy />
              </div>
            )}
          </div>

          <Input
            data-cy="resource-delete-name"
            className="mt-2.5 mb-5"
            onChange={(e) => setDeleteName(e.target.value)}
            disabled={submitting}
          />
        </div>
      )}

      <div className="flex justify-end gap-2.5 mt-5">
        {!confirmByTyping && (
          <Button
            innerRef={cancelRef}
            className="h-auto text-base p-2.5"
            internalType="outline"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
        )}
        <Button
          className="h-auto text-base p-2.5"
          internalType="danger"
          data-cy="resource-confirm-delete"
          onClick={confirmDeleteResource}
          disabled={disabled}
          isLoading={submitting}
        >
          {buttonLabel()}
        </Button>
      </div>
    </Modal>
  )
}

export default DeleteModal
